import {
    MouseEvent as ReactMouseEvent,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    Box,
    CircularProgress,
    ClickAwayListener,
    Collapse,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Typography,
} from '@mui/material';

import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import {
    Copy,
    Download,
    Menu,
    Plus,
    Repeat,
    Settings,
    Sparkles,
    Trash2,
    X,
} from 'lucide-react';

import { enqueueSnackbar } from '~/utils/notifications';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { Features } from '~/components/FeatureToggle';
import InlineEdit from '~/components/InlineEdit';
import StructureSetSettings from '~/components/Header/BreadcrumbNavigation/StructureSetBreadcrumb/StructureSetMenu/StructureSetSettings';
import StructureSetApprovalStatusConfirmationDialog from '~/components/Header/BreadcrumbNavigation/StructureSetBreadcrumb/StructureSetMenu/StructureSetSettings/StructureSetApprovalStatusConfirmationDialog';
import ModelDetails from '~/components/ModelDetails';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import StructureSetItem from '~/components/Structures/StructureSetItem';
import CopyStructureSetDialog from '../CopyStructureSetDialog/CopyStructureSetDialog';

import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { CopyStructureSetResult } from '~/domain/services/structureSetService/structureSetCopyService';
import { CopyTarget } from '../CopyStructureSetDialog/types';
import { openedStructureSetApprovalConfirmationDialogState } from '~/state/atoms/structureSet/roiAtoms';

import { useDicomExport } from '~/hooks/export/useDicomExport';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import { useAvailableStructureSetsView } from '~/hooks/domain/useAvailableStructureSetsView';
import { useLiveStructureSet } from '~/hooks/domain/useLiveStructureSet';
import { useCopyStructureSet } from '~/hooks/structure-set/useCopyStructureSet';
import useCreateStructureSet from '~/hooks/structure-set/useCreateStructureSet';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import useStructureSetSelection from '~/hooks/structure-set/useStructureSetSelection';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { useStructureSetCommands } from '~/hooks/domain/useStructureSetCommands';
import { useStructureSetListView } from '~/hooks/domain/useStructureSetListView';
import { environmentState } from '~/state/atoms/engineAtoms';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import {
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import { WorkflowTypes, workflowModalState } from '~/state/atoms/viewerAtoms';
import AppConfig from '~/utils/appconfig';
import { validateStructureSetLabel } from '~/utils/validation';

type StructureSetHeaderProps = {
    structureSet?: StructureSetReadModel;
};

export default function StructureSetHeader({
    structureSet,
}: StructureSetHeaderProps) {
    const intl = useIntl();
    const env = useRecoilValue(environmentState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const {
        copyStructureSet,
        copyIntoExisting,
        copyWithRegistration,
        copyWithRegistrationIntoExisting,
        copySameFrameOfReference,
        copySameFrameOfReferenceIntoExisting,
    } = useCopyStructureSet();
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isRenamingLabel, setIsRenamingLabel] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const structureSetItems = useStructureSetListView();
    const { rename } = useStructureSetCommands();
    const { setStructureSetApprovalStatus } = useStructureSet();
    const setOpenedStructureSetApprovalConfirmationDialog = useSetRecoilState(
        openedStructureSetApprovalConfirmationDialogState
    );

    const listItem = useMemo(
        () =>
            structureSet
                ? (structureSetItems.find(
                      (i) => i.dicomUid === structureSet.dicomUid
                  ) ?? null)
                : null,
        [structureSetItems, structureSet]
    );

    const validateLabel = (value: string): string | null => {
        const [isValid, error] = validateStructureSetLabel(value.trim());
        return isValid ? null : error;
    };

    const handleLabelRename = (newLabel: string) => {
        const trimmedLabel = newLabel.trim();
        if (structureSet?.structureSetLabel === trimmedLabel) {
            setIsRenamingLabel(false);
            return;
        }
        if (listItem) {
            rename(listItem.id, trimmedLabel);
        }
        setIsRenamingLabel(false);
    };

    const { handleDownloadStructureSet } = useDicomExport();
    const createEmptyStructureSet = useCreateStructureSet();
    const { setEnvironmentStructureSet } = useViewportEnvironment();
    const { deleteStructureSet } = useDeleteDataset();
    const availableStructureSets = useAvailableStructureSetsView(true);
    const setWorkflowModal = useSetRecoilState(workflowModalState);
    const primaryImage = useRecoilValue(primaryImageState);
    const isContourWorkflowEnabled = AppConfig.isFeatureEnabled(
        Features.CONTOUR_WORKFLOW
    );
    const isGenerateDisabled = !selectedPatientId || !primaryImage;
    const { handleStructureSetChange, imageChangeConfirmation } =
        useStructureSetSelection({
            onSelect: () => setIsExpanded(false),
        });

    const patient = useMemo(
        () =>
            selectedPatientId
                ? (patientRepository.getByPatientId(selectedPatientId) ?? null)
                : null,
        [patientRepository, selectedPatientId]
    );

    // Resolve the live StructureSet via the documented escape hatch.
    // The downstream APIs invoked from this header — deleteStructureSet,
    // copyStructureSet, setEnvironmentStructureSet — still take live
    // StructureSet references. Migrating them to ref-based service APIs
    // is a separate follow-up; until then this is the legitimate escape
    // hatch (see useLiveStructureSet's own JSDoc).
    const domainStructureSet = useLiveStructureSet(structureSet?.id);

    const sourceImage = domainStructureSet?.image ?? null;

    const handleToggleProperties = () => {
        setIsPropertiesOpen(!isPropertiesOpen);
        setIsMenuOpen(false);
    };

    const handleHeaderContextMenu = (e: ReactMouseEvent) => {
        if (!structureSet) {
            return;
        }
        e.preventDefault();
        setIsPropertiesOpen(true);
        setIsMenuOpen(false);
    };

    const handleCloseProperties = () => {
        setIsPropertiesOpen(false);
    };

    const handleToggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        setIsPropertiesOpen(false);
    };

    const handleCloseMenu = () => {
        setIsMenuOpen(false);
    };

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        const target = event.target as HTMLElement;
        // Ignore clicks on MUI portal elements (Select dropdown, dialogs)
        // and clicks on <body> which occur when MUI backdrop is dismissed
        if (
            target === document.body ||
            target.closest('[role="presentation"]') ||
            target.closest('[role="dialog"]')
        ) {
            return;
        }
        handleCloseProperties();
        handleCloseMenu();
    };

    const handleDownload = async () => {
        if (!structureSet) {
            return;
        }
        setDownloadLoading(true);
        try {
            await handleDownloadStructureSet(structureSet.dicomUid);
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleDelete = () => {
        if (domainStructureSet) {
            deleteStructureSet(domainStructureSet);
        }
        setIsDeleteDialogOpen(false);
        handleCloseMenu();
    };

    const handleApprovalChipClick = (event: ReactMouseEvent) => {
        if (!structureSet) {
            return;
        }
        event.stopPropagation();
        const nextStatus =
            structureSet.approvalStatus === DICOMApprovalStatus.APPROVED
                ? DICOMApprovalStatus.UNAPPROVED
                : DICOMApprovalStatus.APPROVED;
        setOpenedStructureSetApprovalConfirmationDialog({
            sopUid: structureSet.dicomUid,
            newApprovalStatus: nextStatus,
        });
    };
    const handleOpenCopyDialog = useCallback(() => {
        setCopyDialogOpen(true);
    }, []);
    const handleCloseCopyDialog = useCallback(() => {
        setCopyDialogOpen(false);
    }, []);

    const handleConfirmCopy = useCallback(
        async (target: CopyTarget) => {
            if (!domainStructureSet || !patient || !env) {
                return;
            }

            try {
                let result: CopyStructureSetResult;

                if (target.targetStructureSet) {
                    if (target.registration) {
                        result = await copyWithRegistrationIntoExisting(
                            domainStructureSet,
                            target.targetStructureSet,
                            target.image,
                            target.registration,
                            target.selectedRoiNumbers,
                            target.overwriteExisting
                        );
                    } else if (target.sameFrameOfReference) {
                        result = await copySameFrameOfReferenceIntoExisting(
                            domainStructureSet,
                            target.targetStructureSet,
                            target.image,
                            target.selectedRoiNumbers,
                            target.overwriteExisting
                        );
                    } else {
                        result = await copyIntoExisting(
                            domainStructureSet,
                            target.targetStructureSet,
                            target.selectedRoiNumbers,
                            target.overwriteExisting
                        );
                    }
                } else {
                    if (target.registration) {
                        result = await copyWithRegistration(
                            domainStructureSet,
                            target.image,
                            target.registration,
                            patient,
                            target.selectedRoiNumbers
                        );
                    } else if (target.sameFrameOfReference) {
                        result = await copySameFrameOfReference(
                            domainStructureSet,
                            target.image,
                            patient,
                            target.selectedRoiNumbers
                        );
                    } else {
                        result = await copyStructureSet(
                            domainStructureSet,
                            target.image,
                            patient,
                            target.selectedRoiNumbers
                        );
                    }
                }

                handleCloseCopyDialog();

                setEnvironmentStructureSet(
                    [result.structureSet],
                    patient.dataSet.PatientID
                );

                let emptyCount = 0;
                for (const roiNumber of result.copiedRoiNumbers) {
                    const roi =
                        result.structureSet.dicomStructureSet.getRoi(roiNumber);
                    if (!roi) {
                        continue;
                    }
                    // Skip point ROIs (MARKER, ISOCENTER, REGISTRATION) as they're expected to have no volume
                    if (roi.isPointRoi) {
                        continue;
                    }
                    const hasVolume = roi.contours.some(
                        (c) => c.data.length >= 3
                    );
                    if (!hasVolume) {
                        emptyCount++;
                    }
                }

                const skippedCount = result.skippedRois.length;

                if (skippedCount > 0 && emptyCount > 0) {
                    enqueueSnackbar(
                        `Structures copied successfully. ${skippedCount} structure(s) were skipped and ${emptyCount} structure(s) have empty volumes.`,
                        {
                            variant: 'warning',
                        }
                    );
                } else if (skippedCount > 0) {
                    enqueueSnackbar(
                        `Structures copied successfully. ${skippedCount} structure(s) were skipped.`,
                        {
                            variant: 'warning',
                        }
                    );
                } else if (emptyCount > 0) {
                    enqueueSnackbar(
                        `Structures copied successfully. ${emptyCount} structure(s) have empty volumes.`,
                        {
                            variant: 'warning',
                        }
                    );
                } else {
                    enqueueSnackbar('Structures copied successfully.', {
                        variant: 'success',
                    });
                }
            } catch (error) {
                console.error('Failed to copy structures:', error);
                enqueueSnackbar(
                    `Failed to copy structures: ${error instanceof Error ? error.message : error}`,
                    { variant: 'error' }
                );
            }
        },
        [
            domainStructureSet,
            patient,
            env,
            copyStructureSet,
            copyIntoExisting,
            copyWithRegistration,
            copyWithRegistrationIntoExisting,
            copySameFrameOfReference,
            copySameFrameOfReferenceIntoExisting,
            handleCloseCopyDialog,
            setEnvironmentStructureSet,
        ]
    );

    return (
        <>
            <Box
                ref={headerRef}
                width="100%"
                position="relative"
                data-testid="structure-set-details"
                onContextMenu={
                    structureSet ? handleHeaderContextMenu : undefined
                }
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px={1.5}
                >
                    <Typography fontSize={12} color="primary" fontWeight={500}>
                        <FormattedMessage id="structureSet" />
                    </Typography>
                    {structureSet && (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.25}
                            mr={-0.5}
                        >
                            <IconButton
                                size="small"
                                onClick={handleToggleProperties}
                                selected={isPropertiesOpen}
                                sx={{ padding: 0.5, color: 'primary.main' }}
                                data-testid="structureset-properties-button"
                                aria-label={intl.formatMessage({
                                    id: 'properties',
                                })}
                            >
                                <Settings size={13} />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleToggleMenu}
                                selected={isMenuOpen}
                                sx={{ padding: 0.5, color: 'primary.main' }}
                                data-testid="structureset-menu-button"
                                aria-label={intl.formatMessage({
                                    id: 'structureSetMenu',
                                })}
                            >
                                <Menu size={14} />
                            </IconButton>
                        </Box>
                    )}
                </Box>
                <Box px={1.5}>
                    <Box display="flex" justifyContent="space-between">
                        {isRenamingLabel && structureSet ? (
                            <InlineEdit
                                value={structureSet.structureSetLabel}
                                onSubmit={handleLabelRename}
                                onCancel={() => setIsRenamingLabel(false)}
                                validate={validateLabel}
                                fontSize={12}
                            />
                        ) : (
                            <Typography
                                fontSize={12}
                                noWrap
                                onDoubleClick={
                                    structureSet
                                        ? () => setIsRenamingLabel(true)
                                        : undefined
                                }
                                sx={{
                                    cursor: structureSet
                                        ? 'pointer'
                                        : undefined,
                                }}
                                data-testid="structure-set-label"
                            >
                                {structureSet ? (
                                    <SensitiveInfoTypography>
                                        {structureSet.structureSetLabel}
                                    </SensitiveInfoTypography>
                                ) : (
                                    <FormattedMessage id="unknown.series" />
                                )}
                            </Typography>
                        )}
                        {structureSet && (
                            <ReviewStatus
                                status={structureSet.approvalStatus}
                                fontSize={10}
                                color="text.secondary"
                                onClick={handleApprovalChipClick}
                                tooltip={
                                    <FormattedMessage
                                        id={
                                            structureSet.approvalStatus ===
                                            DICOMApprovalStatus.APPROVED
                                                ? 'structureSet.unapprove.tooltip'
                                                : 'structureSet.approve.tooltip'
                                        }
                                    />
                                }
                                dataTestId="structure-set-approval-chip"
                            />
                        )}
                    </Box>
                    {structureSet?.generationModel && (
                        <ModelDetails
                            generationModel={structureSet.generationModel}
                            fullWidth
                        />
                    )}
                </Box>
            </Box>

            {copyDialogOpen && patient && sourceImage && structureSet && (
                <CopyStructureSetDialog
                    open={copyDialogOpen}
                    onClose={handleCloseCopyDialog}
                    onConfirm={handleConfirmCopy}
                    sourceStructureSet={structureSet}
                    patient={patient}
                    sourceImage={sourceImage}
                />
            )}
            {/* Properties popper (cog icon) */}
            <Popper
                open={isPropertiesOpen && !!structureSet}
                anchorEl={headerRef.current}
                placement="right-start"
                sx={{ zIndex: 1300 }}
                modifiers={[
                    {
                        name: 'offset',
                        options: { offset: [-9, 1] },
                    },
                ]}
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            p: 1.5,
                            bgcolor: theme.palette.background.default,
                            border: `1px solid ${theme.palette.divider}`,
                            borderLeft: 'none',
                            borderRadius: 0,
                        })}
                    >
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                        >
                            <Typography fontSize={12} fontWeight={500}>
                                <FormattedMessage id="properties" />
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={handleCloseProperties}
                                sx={{ p: 0.25 }}
                                aria-label={intl.formatMessage({ id: 'close' })}
                            >
                                <X size={14} />
                            </IconButton>
                        </Box>

                        <StructureSetSettings />
                    </Paper>
                </ClickAwayListener>
            </Popper>

            <Popper
                open={isMenuOpen && !!structureSet}
                anchorEl={headerRef.current}
                placement="right-start"
                sx={{ zIndex: 1300 }}
                modifiers={[
                    {
                        name: 'offset',
                        options: { offset: [-9, 1] },
                    },
                ]}
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            bgcolor: theme.palette.background.default,
                            border: `1px solid ${theme.palette.divider}`,
                            borderLeft: 'none',
                            borderRadius: 0,
                        })}
                    >
                        <MenuList
                            sx={{
                                '& .MuiMenuItem-root': {
                                    px: 2.5,
                                    py: 1,
                                    borderRadius: 0,
                                },
                            }}
                        >
                            {isContourWorkflowEnabled && (
                                <MenuItem
                                    onClick={() => {
                                        setWorkflowModal((state) => ({
                                            ...state,
                                            open: true,
                                            page: WorkflowTypes.CONTOUR,
                                        }));
                                        handleCloseMenu();
                                    }}
                                    disabled={isGenerateDisabled}
                                    data-testid="generate-structureset-button"
                                >
                                    <ListItemIcon>
                                        <Sparkles size={14} />
                                    </ListItemIcon>
                                    <ListItemText>
                                        <FormattedMessage id="generateStructureSet" />
                                    </ListItemText>
                                </MenuItem>
                            )}
                            <MenuItem
                                onClick={() => {
                                    createEmptyStructureSet();
                                    handleCloseMenu();
                                }}
                                data-testid="new-structureset-button"
                            >
                                <ListItemIcon>
                                    <Plus size={14} />
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="createStructureSet" />
                                </ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    handleOpenCopyDialog();
                                    handleCloseMenu();
                                }}
                                disabled={!domainStructureSet || !sourceImage}
                                data-testid="copy-structure-set-menu-item"
                            >
                                <ListItemIcon>
                                    <Copy size={14} />
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage
                                        id="structureSet.copy"
                                        defaultMessage="Copy to..."
                                    />
                                </ListItemText>
                            </MenuItem>
                            {availableStructureSets.length > 1 && (
                                <>
                                    <MenuItem
                                        onClick={() =>
                                            setIsExpanded(!isExpanded)
                                        }
                                        data-testid="change-structureset-button"
                                        selected={isExpanded}
                                    >
                                        <ListItemIcon>
                                            <Repeat size={14} />
                                        </ListItemIcon>
                                        <ListItemText>
                                            <FormattedMessage id="changeStructureSet" />
                                        </ListItemText>
                                    </MenuItem>
                                    <Collapse in={isExpanded}>
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            maxHeight={300}
                                            overflow="auto"
                                        >
                                            {availableStructureSets.map(
                                                (set) => (
                                                    <StructureSetItem
                                                        key={set.sopInstanceUid}
                                                        structureSet={set}
                                                        isActive={
                                                            structureSet?.dicomUid ===
                                                            set.sopInstanceUid
                                                        }
                                                        onClick={() =>
                                                            handleStructureSetChange(
                                                                set
                                                            )
                                                        }
                                                    />
                                                )
                                            )}
                                        </Box>
                                    </Collapse>
                                </>
                            )}
                            <MenuItem
                                onClick={handleDownload}
                                disabled={downloadLoading}
                            >
                                <ListItemIcon>
                                    {downloadLoading ? (
                                        <CircularProgress
                                            size={14}
                                            color="primary"
                                        />
                                    ) : (
                                        <Download size={14} />
                                    )}
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="downloadStructures" />
                                </ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={() => setIsDeleteDialogOpen(true)}
                                data-testid="delete-structureset-button"
                            >
                                <ListItemIcon>
                                    <Trash2 size={14} />
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="deleteStructureSet" />
                                </ListItemText>
                            </MenuItem>
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>

            <ConfirmationDialog
                open={imageChangeConfirmation.open}
                title={<FormattedMessage id="confirmImageChange.title" />}
                message={<FormattedMessage id="confirmImageChange.message" />}
                onConfirm={imageChangeConfirmation.onConfirm}
                onCancel={imageChangeConfirmation.onCancel}
                mode="primary"
            />
            <ConfirmationDialog
                open={isDeleteDialogOpen}
                title={<FormattedMessage id="deleteStructureSet" />}
                message={<FormattedMessage id="deleteStructureSetConfirm" />}
                onConfirm={handleDelete}
                onCancel={() => setIsDeleteDialogOpen(false)}
                confirmText={<FormattedMessage id="delete" />}
                cancelText={<FormattedMessage id="cancel" />}
                mode="error"
            />
            <StructureSetApprovalStatusConfirmationDialog
                onApprovalStatusChange={setStructureSetApprovalStatus}
            />
        </>
    );
}
